import { useState } from "react";
import { Mail, Loader2, Send } from "lucide-react";

const TEMPLATES = [
  {
    label: "Schedule a Call",
    subject: "Let's schedule your strategy call — Silent Sentry, LLC",
    message: (name) => `Hi ${name},\n\nThank you for your inquiry. I'd like to schedule a brief call to walk through your objectives and outline next steps for your confidential assessment.\n\nWhat does your availability look like this week? A 20-minute call is usually all we need to get started.\n\nBest regards,\nSilent Sentry Advisory Practice`,
  },
  {
    label: "Request Details",
    subject: "A few details to prepare your assessment — Silent Sentry, LLC",
    message: (name) => `Hi ${name},\n\nThanks for reaching out. To prepare an accurate, vendor-neutral recommendation, could you share:\n\n1. Number of locations/sites in scope\n2. Current provider(s) and contract end dates\n3. Any compliance requirements (CMMC, NIST, HIPAA, PCI)\n\nReply here and we'll take it from there.\n\nBest regards,\nSilent Sentry Advisory Practice`,
  },
  {
    label: "Proposal Underway",
    subject: "Your tailored recommendation is in progress — Silent Sentry, LLC",
    message: (name) => `Hi ${name},\n\nGood news — we've completed our initial supplier evaluation for your requirements and are finalizing a tailored recommendation with negotiated pricing.\n\nExpect it within 2 business days. Happy to walk through it live once it's in your inbox.\n\nBest regards,\nSilent Sentry Advisory Practice`,
  },
  {
    label: "Internet Availability",
    subject: "Internet & TV availability at your site — Silent Sentry, LLC",
    message: (name) => `Hi ${name},\n\nWe've checked carrier coverage for your location. There are several strong options available — including dedicated fiber, cable, and satellite backup — and we can negotiate pricing across all of them.\n\nCan we set up a quick call to review the options and pricing?\n\nBest regards,\nSilent Sentry Service Desk`,
  },
];

export const ReplySection = ({ prospectEmail, prospectName, onSendReply }) => {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("Re: Your inquiry — Silent Sentry, LLC");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const applyTemplate = (t) => {
    const firstName = (prospectName || "").split(" ")[0] || "there";
    setSubject(t.subject);
    setMessage(t.message(firstName));
    setOpen(true);
  };

  const send = async () => {
    setSending(true);
    const ok = await onSendReply(subject.trim(), message.trim());
    setSending(false);
    if (ok) {
      setMessage("");
      setOpen(false);
    }
  };

  return (
    <div data-testid="reply-section" className="mt-8 border-t border-white/10 pt-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-medium flex items-center gap-2">
          <Mail className="w-4 h-4 text-cyan-400" /> Reply to prospect
        </h3>
        {!open && (
          <button data-testid="open-reply-btn" onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 text-sm bg-cyan-400 text-[#05071F] font-semibold px-4 py-2 rounded-full hover:bg-cyan-300 transition-colors">
            <Send className="w-4 h-4" /> Compose Reply
          </button>
        )}
      </div>
      {open && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-slate-500 uppercase tracking-widest self-center mr-1">Templates:</span>
            {TEMPLATES.map((t) => (
              <button key={t.label} type="button" data-testid={`reply-template-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => applyTemplate(t)}
                className="text-xs border border-violet-400/40 text-violet-300 px-3 py-1.5 rounded-full hover:bg-violet-400/10 transition-colors">
                {t.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500">To: <span className="text-slate-300">{prospectEmail}</span> · sent from Silent Sentry with reply-to janthony@silentsentrycorp.com</p>
          <input data-testid="reply-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            className="w-full bg-[#05071F] border border-white/15 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-400" />
          <textarea data-testid="reply-message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={5}
            placeholder="Write your reply… (it will be wrapped in the branded Silent Sentry email template)"
            className="w-full bg-[#05071F] border border-white/15 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-400 resize-none" />
          <div className="flex gap-3 justify-end">
            <button data-testid="cancel-reply-btn" onClick={() => setOpen(false)}
              className="text-sm border border-white/20 text-slate-300 px-5 py-2 rounded-full hover:border-white/50 transition-colors">
              Cancel
            </button>
            <button data-testid="send-reply-btn" onClick={send} disabled={sending || !subject.trim() || !message.trim()}
              className="inline-flex items-center gap-2 text-sm bg-cyan-400 text-[#05071F] font-semibold px-5 py-2 rounded-full hover:bg-cyan-300 transition-colors disabled:opacity-50">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Reply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
